import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ExternalLink, Loader2, Save, Search } from 'lucide-react';
import type { MediaItem, ProjectFile } from '../../types';
import {
  isCatalogHtmlMediaItem,
  subscribeRecentMediaItemsForCatalog,
  updateMediaItem,
} from '../../utils/mediaLibrary';
import {
  isCatalogHtmlProjectFile,
  subscribeRecentProjectFilesForCatalog,
  updateProjectFileHtmlDescriptors,
} from '../../utils/projectFileService';
import './SiteHtmlModelsPanel.css';

export type SiteHtmlCatalogRow =
  | { kind: 'project'; file: ProjectFile }
  | { kind: 'media'; item: MediaItem };

function splitDescriptors(raw: string): string[] {
  return raw
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function rowSortTime(r: SiteHtmlCatalogRow): number {
  return r.kind === 'project' ? r.file.uploadedAt.getTime() : r.item.createdAt.getTime();
}

function descriptorsFromRow(r: SiteHtmlCatalogRow): string[] {
  if (r.kind === 'project') return r.file.htmlModelDescriptors ?? [];
  return r.item.htmlModelDescriptors ?? [];
}

/** Static screenshot: catalog field first, then legacy image thumbnail on media items. */
function catalogPreviewUrl(r: SiteHtmlCatalogRow): string | undefined {
  if (r.kind === 'project') {
    const u = r.file.htmlPreviewImageUrl?.trim();
    return u || undefined;
  }
  const h = r.item.htmlPreviewImageUrl?.trim();
  if (h) return h;
  const t = r.item.thumbnailUrl?.trim();
  return t || undefined;
}

function HtmlModelPreviewPlaceholder(): React.ReactElement {
  return (
    <div className="shm-preview-fallback" aria-hidden>
      <svg className="shm-preview-fallback-svg" viewBox="0 0 160 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="shm-bg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1e293b" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>
        </defs>
        <rect width="160" height="100" fill="url(#shm-bg)" rx="6" />
        <rect x="28" y="22" width="104" height="56" fill="none" stroke="#475569" strokeWidth="1.5" rx="4" />
        <path d="M44 68 L58 48 L72 58 L88 38 L104 56 L118 44" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="118" cy="32" r="5" fill="#334155" />
      </svg>
      <span className="shm-preview-fallback-label">HTML model</span>
    </div>
  );
}

interface SiteHtmlModelsPanelProps {
  projectNameById: (projectId: string) => string | undefined;
}

const HtmlModelCard: React.FC<{
  row: SiteHtmlCatalogRow;
  projectLabel?: string;
  onSave: (row: SiteHtmlCatalogRow, descriptors: string[], previewUrl: string) => Promise<void>;
}> = ({ row, projectLabel, onSave }) => {
  const [draftDesc, setDraftDesc] = useState(() => descriptorsFromRow(row).join(', '));
  const [draftPreview, setDraftPreview] = useState(() => catalogPreviewUrl(row) ?? '');
  const [saving, setSaving] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);

  useEffect(() => {
    setDraftDesc(descriptorsFromRow(row).join(', '));
    setDraftPreview(catalogPreviewUrl(row) ?? '');
    setImgBroken(false);
  }, [row]);

  const url = row.kind === 'project' ? row.file.url : row.item.url;
  const title = row.kind === 'project' ? row.file.name : row.item.name;
  const openLabel = row.kind === 'project' ? 'Project file' : 'Media library';
  const previewSrc = catalogPreviewUrl(row);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(row, splitDescriptors(draftDesc), draftPreview);
    } finally {
      setSaving(false);
    }
  };

  const rowKey = row.kind === 'project' ? row.file.id : row.item.id;

  return (
    <article className="shm-card">
      <div className="shm-card-preview">
        {previewSrc && !imgBroken ? (
          <img
            className="shm-preview-img"
            src={previewSrc}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImgBroken(true)}
          />
        ) : (
          <HtmlModelPreviewPlaceholder />
        )}
      </div>
      <div className="shm-card-body">
        <div className="shm-card-head">
          <h3 className="shm-card-title" title={title}>
            {title}
          </h3>
          <span className="shm-badge">{openLabel}</span>
        </div>
        {projectLabel && (
          <p className="shm-meta">
            <span className="shm-meta-k">Project</span> {projectLabel}
          </p>
        )}
        {row.kind === 'project' && row.file.versionLabel && (
          <p className="shm-meta">
            <span className="shm-meta-k">Version</span> {row.file.versionLabel}
          </p>
        )}
        <p className="shm-meta">
          <span className="shm-meta-k">Uploaded</span>{' '}
          {format(row.kind === 'project' ? row.file.uploadedAt : row.item.createdAt, 'MMM d, yyyy · h:mm a')}
        </p>
        <div className="shm-desc-block">
          <label className="shm-label" htmlFor={`shm-shot-${rowKey}`}>
            Screenshot URL (optional)
          </label>
          <input
            id={`shm-shot-${rowKey}`}
            type="url"
            className="shm-preview-url-input"
            value={draftPreview}
            onChange={(e) => setDraftPreview(e.target.value)}
            placeholder="https://… (hosted image of the model)"
          />
          <label className="shm-label" htmlFor={`shm-desc-${rowKey}`}>
            Search descriptors
          </label>
          <textarea
            id={`shm-desc-${rowKey}`}
            className="shm-desc-input"
            rows={2}
            value={draftDesc}
            onChange={(e) => setDraftDesc(e.target.value)}
            placeholder="e.g. timber frame, great room, revision A — comma or semicolon separated"
          />
          <div className="shm-desc-actions">
            <button type="button" className="shm-btn-save" disabled={saving} onClick={() => void handleSave()}>
              {saving ? <Loader2 size={14} className="shm-spin" /> : <Save size={14} />}
              Save
            </button>
            <a className="shm-open-link" href={url} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} />
              Open HTML
            </a>
          </div>
        </div>
      </div>
    </article>
  );
};

const SiteHtmlModelsPanel: React.FC<SiteHtmlModelsPanelProps> = ({ projectNameById }) => {
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const unsubP = subscribeRecentProjectFilesForCatalog(setProjectFiles, (e) => setLoadErr(e.message));
    const unsubM = subscribeRecentMediaItemsForCatalog(setMediaItems, (e) => setLoadErr((prev) => prev || e.message));
    return () => {
      unsubP();
      unsubM();
    };
  }, []);

  const mergedRows = useMemo((): SiteHtmlCatalogRow[] => {
    const rows: SiteHtmlCatalogRow[] = [
      ...projectFiles.filter(isCatalogHtmlProjectFile).map((file) => ({ kind: 'project' as const, file })),
      ...mediaItems.filter(isCatalogHtmlMediaItem).map((item) => ({ kind: 'media' as const, item })),
    ];
    rows.sort((a, b) => rowSortTime(b) - rowSortTime(a));
    return rows;
  }, [projectFiles, mediaItems]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mergedRows;
    return mergedRows.filter((r) => {
      const name = (r.kind === 'project' ? r.file.name : r.item.name).toLowerCase();
      const desc = (descriptorsFromRow(r).join(' ') || '').toLowerCase();
      const shot = (catalogPreviewUrl(r) || '').toLowerCase();
      const proj =
        r.kind === 'project'
          ? (projectNameById(r.file.projectId) || r.file.projectId || '').toLowerCase()
          : '';
      const ver = r.kind === 'project' && r.file.versionLabel ? r.file.versionLabel.toLowerCase() : '';
      const tags =
        r.kind === 'media' ? (r.item.tags || []).join(' ').toLowerCase() + (r.item.description || '').toLowerCase() : '';
      return (
        name.includes(q) ||
        desc.includes(q) ||
        shot.includes(q) ||
        proj.includes(q) ||
        ver.includes(q) ||
        tags.includes(q)
      );
    });
  }, [mergedRows, search, projectNameById]);

  const saveRow = useCallback(async (row: SiteHtmlCatalogRow, descriptors: string[], previewUrl: string) => {
    const prev = previewUrl.trim();
    if (row.kind === 'project') {
      if (!row.file.id) return;
      await updateProjectFileHtmlDescriptors(row.file.id, descriptors, { htmlPreviewImageUrl: prev });
      return;
    }
    if (!row.item.id) return;
    await updateMediaItem(row.item.id, {
      htmlModelDescriptors: descriptors,
      htmlPreviewImageUrl: prev || '',
    });
  }, []);

  return (
    <div className="shm-panel">
      <header className="shm-toolbar">
        <div className="shm-search">
          <Search size={16} className="shm-search-icon" aria-hidden />
          <input
            type="search"
            className="shm-search-input"
            placeholder="Search by name, project, version, screenshot URL, or descriptors…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Filter HTML models"
          />
        </div>
        <p className="shm-hint">
          Showing {filteredRows.length} of {mergedRows.length} HTML files from project uploads and the media library.
          Cards use a static image when you set a screenshot URL; otherwise a generic placeholder (no live 3D load in the
          grid).
        </p>
      </header>
      {loadErr && <div className="shm-error-banner">{loadErr}</div>}
      <div className="shm-grid">
        {filteredRows.map((row) => {
          const key = row.kind === 'project' ? `p-${row.file.id}` : `m-${row.item.id}`;
          const projectLabel =
            row.kind === 'project' ? projectNameById(row.file.projectId) || row.file.projectId : undefined;
          return <HtmlModelCard key={key} row={row} projectLabel={projectLabel} onSave={saveRow} />;
        })}
      </div>
      {filteredRows.length === 0 && !loadErr && <p className="shm-empty">No HTML models match your search.</p>}
    </div>
  );
};

export default SiteHtmlModelsPanel;
